import React, { useState, useEffect, useCallback, useImperativeHandle } from 'react';
import {
  FiCloud,
  FiRefreshCw,
  FiCheckCircle,
  FiAlertTriangle,
  FiXCircle,
  FiClock,
  FiExternalLink,
  FiChevronDown,
  FiChevronUp,
  FiLink,
  FiUploadCloud,
} from 'react-icons/fi';
import {
  getBedrockMappings,
  syncBedrockMapping,
  resolveBedrockConflict,
  syncPromptToBedrock,
} from '../../../../../services/aiOperationsApi';
import BedrockErrorAlert from './BedrockErrorAlert';

type SyncStatusType = 'synced' | 'pending' | 'external_changes' | 'error' | 'not_linked';

interface BedrockMapping {
  id: string;
  aivilo_prompt_id: string;
  bedrock_prompt_arn?: string;
  bedrock_current_version?: string;
  has_external_changes?: boolean;
  last_error_at?: string;
  last_error_message?: string;
  consecutive_failures?: number;
  last_push_at?: string;
  last_pull_at?: string;
  sync_direction?: string;
}

interface BedrockSyncStatusProps {
  promptId: string;
  onExternalChanges?: (mapping: BedrockMapping) => void;
  onSyncComplete?: () => void;
  onStatusChange?: (status: SyncStatusType) => void;
  className?: string;
}

export interface BedrockSyncStatusHandle {
  refresh: () => Promise<void>;
  getSyncStatus: () => SyncStatusType;
}

interface SyncStatusBadgeProps {
  status: string;
}

/**
 * Status badge for sync state
 */
const SyncStatusBadge: React.FC<SyncStatusBadgeProps> = ({ status }) => {
  const config = {
    synced: {
      icon: FiCheckCircle,
      bg: 'bg-green-100 dark:bg-green-900/30',
      text: 'text-green-700 dark:text-green-400',
      label: 'Synced',
    },
    pending: {
      icon: FiClock,
      bg: 'bg-amber-100 dark:bg-amber-900/30',
      text: 'text-amber-700 dark:text-amber-400',
      label: 'Pending Sync',
    },
    external_changes: {
      icon: FiAlertTriangle,
      bg: 'bg-orange-100 dark:bg-orange-900/30',
      text: 'text-orange-700 dark:text-orange-400',
      label: 'External Changes',
    },
    error: {
      icon: FiXCircle,
      bg: 'bg-red-100 dark:bg-red-900/30',
      text: 'text-red-700 dark:text-red-400',
      label: 'Sync Error',
    },
    not_linked: {
      icon: FiLink,
      bg: 'bg-gray-100 dark:bg-gray-700',
      text: 'text-gray-600 dark:text-gray-400',
      label: 'Not Linked',
    },
  };

  const { icon: Icon, bg, text, label } = config[status as keyof typeof config] || config.not_linked;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${bg} ${text}`}>
      <Icon className="w-3.5 h-3.5" />
      {label}
    </span>
  );
};

/**
 * Format relative time
 */
const formatRelativeTime = (dateString: string | null | undefined): string => {
  if (!dateString) return 'Never';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
};

/**
 * BedrockSyncStatus Component
 *
 * Displays Bedrock sync status in the prompt detail sidebar.
 * Shows ARN, version, last sync time, and sync actions.
 */
const BedrockSyncStatus = React.forwardRef<BedrockSyncStatusHandle, BedrockSyncStatusProps>(({
  promptId,
  onExternalChanges,
  onSyncComplete,
  onStatusChange,
  className = '',
}, ref) => {
  const [mapping, setMapping] = useState<BedrockMapping | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [initialSyncing, setInitialSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  // Fetch mapping data for this prompt
  const fetchMapping = useCallback(async () => {
    if (!promptId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await getBedrockMappings();
      const mappings = response.data || response.mappings || [];

      // Find mapping for this prompt
      const promptMapping = mappings.find(
        (m: BedrockMapping) => m.aivilo_prompt_id === promptId
      );

      setMapping(promptMapping || null);

      // Notify parent if there are external changes
      if (promptMapping?.has_external_changes && onExternalChanges) {
        onExternalChanges(promptMapping);
      }
    } catch (err: unknown) {
      console.error('Failed to fetch Bedrock mapping:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [promptId, onExternalChanges]);

  useEffect(() => {
    fetchMapping();
  }, [fetchMapping]);

  // Expose refresh and sync status to parent via ref
  useImperativeHandle(ref, () => ({
    refresh: fetchMapping,
    getSyncStatus: () => getSyncStatus(),
  }));

  // Handle sync action
  const handleSync = async (direction: string = 'pull') => {
    if (!mapping?.id) return;

    try {
      setSyncing(true);
      setError(null);

      await syncBedrockMapping(mapping.id, direction);
      await fetchMapping();

      if (onSyncComplete) {
        onSyncComplete();
      }
    } catch (err: unknown) {
      console.error('Sync failed:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setSyncing(false);
    }
  };

  // Handle conflict resolution
  const handleResolveConflict = async (resolution: string) => {
    if (!mapping?.id) return;

    try {
      setSyncing(true);
      setError(null);

      await resolveBedrockConflict(mapping.id, resolution);
      await fetchMapping();

      if (onSyncComplete) {
        onSyncComplete();
      }
    } catch (err: unknown) {
      console.error('Conflict resolution failed:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setSyncing(false);
    }
  };

  // Handle initial sync to Bedrock (for prompts not yet linked)
  const handleInitialSync = async () => {
    if (!promptId) return;

    try {
      setInitialSyncing(true);
      setError(null);

      const result = await syncPromptToBedrock(promptId);

      // Check if sync was successful
      if (result?.success === false) {
        setError(result.message || 'Sync failed. Check if Bedrock credentials are configured.');
        return;
      }

      await fetchMapping();

      if (onSyncComplete) {
        onSyncComplete();
      }
    } catch (err: unknown) {
      console.error('Initial sync to Bedrock failed:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setInitialSyncing(false);
    }
  };

  // Determine sync status
  const getSyncStatus = useCallback((): SyncStatusType => {
    if (!mapping) return 'not_linked';
    if (mapping.has_external_changes) return 'external_changes';
    if (mapping.last_error_at && (mapping.consecutive_failures ?? 0) > 0) return 'error';
    if (mapping.last_push_at || mapping.last_pull_at) return 'synced';
    return 'pending';
  }, [mapping]);

  // Report status changes to parent
  useEffect(() => {
    if (!loading && onStatusChange) {
      onStatusChange(getSyncStatus());
    }
  }, [loading, getSyncStatus, onStatusChange]);

  // Not linked to Bedrock
  if (!loading && !mapping) {
    return (
      <div className={`bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
            <FiCloud className="w-4 h-4" />
            <span className="text-sm font-medium">Bedrock Integration</span>
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
          This prompt is not linked to AWS Bedrock.
        </p>
        {error && (
          <div className="mt-2">
            <BedrockErrorAlert error={error} compact />
          </div>
        )}
        <button
          onClick={handleInitialSync}
          disabled={initialSyncing}
          className="mt-3 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors"
        >
          <FiUploadCloud className={`w-3.5 h-3.5 ${initialSyncing ? 'animate-pulse' : ''}`} />
          {initialSyncing ? 'Syncing...' : 'Sync to Bedrock'}
        </button>
      </div>
    );
  }

  const syncStatus = getSyncStatus();

  return (
    <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg ${className}`}>
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-2">
          <FiCloud className="w-4 h-4 text-blue-500" />
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            Bedrock Sync
          </span>
        </div>
        <div className="flex items-center gap-2">
          {loading ? (
            <FiRefreshCw className="w-4 h-4 animate-spin text-gray-400" />
          ) : (
            <SyncStatusBadge status={syncStatus} />
          )}
          {expanded ? (
            <FiChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <FiChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {expanded && mapping && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-100 dark:border-gray-700">
          {/* Error message */}
          {error && (
            <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs text-red-600 dark:text-red-400">
              {error}
            </div>
          )}

          {/* Bedrock Info */}
          <div className="mt-3 space-y-2">
            {/* ARN */}
            <div className="flex items-start gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 w-16 flex-shrink-0">ARN</span>
              <div className="flex-1 min-w-0">
                <code className="text-xs text-gray-700 dark:text-gray-300 break-all bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                  {mapping.bedrock_prompt_arn || 'N/A'}
                </code>
              </div>
            </div>

            {/* Version */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 w-16">Version</span>
              <span className="text-xs text-gray-700 dark:text-gray-300">
                {mapping.bedrock_current_version || 'DRAFT'}
              </span>
            </div>

            {/* Last Sync */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 w-16">Last Sync</span>
              <span className="text-xs text-gray-700 dark:text-gray-300">
                {formatRelativeTime(mapping.last_pull_at || mapping.last_push_at)}
              </span>
            </div>

            {/* Sync Direction */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 w-16">Direction</span>
              <span className="text-xs text-gray-700 dark:text-gray-300 capitalize">
                {mapping.sync_direction || 'bidirectional'}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
            {/* Sync Now Button */}
            <button
              onClick={() => handleSync('pull')}
              disabled={syncing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-50"
            >
              <FiRefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Now'}
            </button>

            {/* View in AWS Console */}
            {mapping.bedrock_prompt_arn && (
              <a
                href={`https://console.aws.amazon.com/bedrock/home?region=${mapping.bedrock_prompt_arn.split(':')[3]}#/prompt-management`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <FiExternalLink className="w-3.5 h-3.5" />
                AWS Console
              </a>
            )}
          </div>

          {/* External Changes Warning */}
          {mapping.has_external_changes && (
            <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
              <div className="flex items-start gap-2">
                <FiAlertTriangle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-orange-700 dark:text-orange-400">
                    External Changes Detected
                  </p>
                  <p className="text-xs text-orange-600 dark:text-orange-400/80 mt-1">
                    This prompt was modified directly in AWS Bedrock.
                  </p>
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => handleResolveConflict('use_bedrock')}
                      disabled={syncing}
                      className="px-2 py-1 text-xs font-medium text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-900/30 rounded hover:bg-orange-200 dark:hover:bg-orange-900/50 disabled:opacity-50"
                    >
                      Use Bedrock Version
                    </button>
                    <button
                      onClick={() => handleResolveConflict('use_aivilo')}
                      disabled={syncing}
                      className="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                    >
                      Keep Aivilo Version
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error Details */}
          {mapping.last_error_at && (mapping.consecutive_failures ?? 0) > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>{mapping.consecutive_failures} consecutive failure{mapping.consecutive_failures === 1 ? '' : 's'}</span>
                <span>Last attempt: {formatRelativeTime(mapping.last_error_at)}</span>
              </div>
              <BedrockErrorAlert
                error={mapping.last_error_message || 'Unknown error occurred'}
                compact
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
});

BedrockSyncStatus.displayName = 'BedrockSyncStatus';

export { SyncStatusBadge };
export default BedrockSyncStatus;
